<template>
  <div class="week">
    <div class="week-content" :class="{ 'week-content--scroll': showAllDays }">
      <DietWeekView
        v-if="currentView"
        :average-active="averageActive"
        :view="currentView"
        :visible-days-count="currentVisibleDaysCount"
      >
        <template #header>
          <div v-if="showAllDays" class="week-headers">
            <DietWeekHeader
              v-for="week in weekIndexes"
              :key="week"
              :copied-week="copiedWeekStatus"
              :editable="canDietBeEdited"
              :open="activeWeekMenu === week"
              :style="{ flex: getWeekDayCount(daysCount, week) }"
              :week="week"
              @copy="copyWeek(week)"
              @paste="pasteWeek(week)"
              @toggle="toggleWeekMenu(week)"
            />
          </div>
          <DietWeekHeader
            v-else
            :copied-week="copiedWeekStatus"
            :editable="canDietBeEdited"
            :open="activeWeekMenu === activeWeek"
            :week="activeWeek"
            @copy="copyWeek(activeWeek)"
            @paste="pasteWeek(activeWeek)"
            @toggle="toggleWeekMenu(activeWeek)"
          />
        </template>
      </DietWeekView>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { A, G } from '@mobily/ts-belt'
  import { tryOnScopeDispose } from '@vueuse/core'
  import { storeToRefs } from 'pinia'
  import { computed, onMounted, ref, watch } from 'vue'
  import { useRoute } from 'vue-router'

  import { useDietDetailProvider } from '@/pages/diets/provider'
  import { dietsService } from '@/services/dietsService'
  import { useDietsStore } from '@/store/dietsStore'
  import { useGlobalStore } from '@/store/globalStore'
  import { getDietDaysCount, getDietWeeksCount, getWeekDayCount } from '@/utils/dietDays'
  import {
    basicNutrients as BasicNutrients,
    specificNutrients as SpecificNutrients,
  } from '@/utils/nutrients'

  import { getWeekStartDayIndex } from './buildDietWeekView'
  import DietWeekHeader from './components/DietWeekHeader/DietWeekHeader.vue'
  import DietWeekView from './components/DietWeekView/DietWeekView.vue'

  const globalStore = useGlobalStore()
  const dietStore = useDietsStore()
  const route = useRoute()
  const {
    averageActive,
    canDietBeEdited,
    copiedWeekStatus,
    diet,
    lossesMultiplier,
    relatedDishes,
    activeRepeatedDishNames,
  } = storeToRefs(dietStore)
  const { showAllDays } = useDietDetailProvider()

  const unusedDishesMessageId = ref<number>()
  const activeWeekMenu = ref<number>()

  const daysCount = computed(() => getDietDaysCount(diet.value))
  const weekIndexes = computed(() =>
    Array.from({ length: getDietWeeksCount(daysCount.value) }, (_, index) => index + 1),
  )

  const activeWeek = computed(() => {
    const week = parseInt(route.query.week?.toString() ?? '1')

    return weekIndexes.value.includes(week) ? week : 1
  })
  const activeWeekDaysCount = computed(() => getWeekDayCount(daysCount.value, activeWeek.value))
  const toggleWeekMenu = (week: number) => {
    activeWeekMenu.value = activeWeekMenu.value === week ? undefined : week
  }
  const copyWeek = (week: number) => {
    dietStore.copyWeek(getWeekStartDayIndex(week), getWeekDayCount(daysCount.value, week))
    activeWeekMenu.value = undefined
  }
  const pasteWeek = (week: number) => {
    dietStore.pasteWeek(getWeekStartDayIndex(week), getWeekDayCount(daysCount.value, week))
    activeWeekMenu.value = undefined
  }
  const getDaysView = (startDayIndex: number, viewDaysCount: number) => {
    if (!diet.value) return undefined

    const endDayIndex = startDayIndex + viewDaysCount
    const meals = diet.value.data.meals.map(meal => ({
      ...meal,
      days: meal.days.slice(startDayIndex, endDayIndex),
    }))

    const schedule = dietsService.getSchedule(
      meals,
      diet.value.data.norms.calories,
      diet.value.data.energy_distribution,
      diet.value.data.choosen_nutrients,
      lossesMultiplier.value,
      activeRepeatedDishNames.value,
    )

    schedule.days = schedule.days.slice(0, viewDaysCount)

    const dayNutrients = dietsService.getDayNutrients(
      {
        ...diet.value,
        data: {
          ...diet.value.data,
          meals,
        },
      },
      lossesMultiplier.value,
    )
    const chosenNutrientsKeys = A.difference(diet.value.data.choosen_nutrients, BasicNutrients)
    const specificNutrientsKeys = A.difference(SpecificNutrients, diet.value.data.choosen_nutrients)

    return {
      basicNutrients: dietsService.getNutrients(
        BasicNutrients,
        dayNutrients,
        diet.value.data.norms,
        dietNormsPermission.value,
      ),
      chosenNutrients: dietsService.getNutrients(
        chosenNutrientsKeys,
        dayNutrients,
        diet.value.data.norms,
        dietNormsPermission.value,
      ),
      schedule,
      specificNutrients: dietsService.getNutrients(
        specificNutrientsKeys,
        dayNutrients,
        diet.value.data.norms,
        dietNormsPermission.value,
      ),
      startDayIndex,
    }
  }

  const dietNormsPermission = computed(() => globalStore.hasPerm('accounts.diet_norms_edit_values'))
  const activeWeekView = computed(() =>
    getDaysView(getWeekStartDayIndex(activeWeek.value), activeWeekDaysCount.value),
  )
  const allDaysView = computed(() => getDaysView(0, daysCount.value))
  const currentView = computed(() => (showAllDays.value ? allDaysView.value : activeWeekView.value))
  const currentVisibleDaysCount = computed(() =>
    showAllDays.value ? daysCount.value : activeWeekDaysCount.value,
  )

  const unusedDishesInfo = computed(() => {
    if (relatedDishes.value) {
      return Object.values(relatedDishes.value)
        .filter(dish => dish.unusedPortions !== 0)
        .map(dish => `${dish.name} x ${dish.unusedPortions}`)
        .sort((a, b) => {
          // sort by name
          const aName = a
            .toLowerCase()
            .normalize('NFD')
            .replace(/\u0142/g, 'l')
          const bName = b
            .toLowerCase()
            .normalize('NFD')
            .replace(/\u0142/g, 'l')
          if (aName < bName) return -1
          if (aName > bName) return 1
          return 0
        })
        .join('<br/>')
    }
    return ''
  })

  const showUnusedDishesInfo = () => {
    const id = unusedDishesMessageId.value
    if (unusedDishesInfo.value !== '') {
      if (G.isNullable(id)) {
        unusedDishesMessageId.value = globalStore.createMessage({
          content: unusedDishesInfo.value,
          timeout: 0,
          title: 'Niewykorzystane porcje posiłków',
        })
      } else {
        globalStore.updateMessage({
          content: unusedDishesInfo.value,
          id,
        })
      }
    } else {
      if (G.isNotNullable(id)) {
        globalStore.deleteMessage(id)
      }
    }
  }

  watch(unusedDishesInfo, (newInfo, oldInfo) => {
    if (newInfo !== oldInfo) {
      showUnusedDishesInfo()
    }
  })

  watch(activeWeek, () => {
    // The router clears all messages before changing the week query parameter.
    // Recreate this persistent message after the navigation completes.
    unusedDishesMessageId.value = undefined
    showUnusedDishesInfo()
  })

  onMounted(() => {
    dietStore.clearUndoRedo()
    showUnusedDishesInfo()
  })

  tryOnScopeDispose(
    globalStore.$onAction(({ name, args }) => {
      if (name === 'deleteMessage') {
        if (unusedDishesMessageId.value === args[0]) {
          unusedDishesMessageId.value = undefined
        }
      }
      if (name === 'deleteMessages') {
        unusedDishesMessageId.value = undefined
      }
    }),
  )
</script>

<style scoped>
  .week {
    width: 100%;
    min-width: 0;
    margin-bottom: 24px;
    overflow: visible;
    background: #fff;
  }

  .week-content--scroll {
    display: block;
    width: 100%;
    min-width: 0;
    padding-bottom: 8px;
    overflow: visible;
  }

  .week-headers {
    display: flex;
    align-items: center;
    min-width: 0;
  }
</style>
